import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions, auth } from '../firebase.js';
import { signOut } from 'firebase/auth';

// ─────────────────────────────────────────────────────────────────────────
// Single-session enforcement — "one account, one login at a time." See
// functions/session.js for the full server-side design and reasoning.
//
// registerNewLoginSession is called ONLY from an explicit sign-in
// (src/lib/auth.js signInWithGoogle/signUpWithEmail/signInWithEmail) —
// deliberately NEVER on a page reload of an already-signed-in tab. That
// distinction matters: this browser's sessionId lives in localStorage,
// shared by every tab of the same browser on the same device, so a reload
// or a second tab just reads the SAME id back rather than minting a new
// one — otherwise reloading a tab would kick out every OTHER real tab of
// your own, which isn't "another login," it's the same one.
//
// startSessionGuard is called on every app load where a user is signed in
// (src/main.js watchAuth), reload or fresh sign-in alike. It watches this
// account's one session doc in real time; the moment a DIFFERENT browser
// registers a new session (because someone else, or the same person from
// another device, just signed in), this tab's listener sees the mismatch
// and signs itself out locally.
//
// A tab that reaches signed-in state WITHOUT ever calling
// registerNewLoginSession itself (a reload of a session that predates this
// feature, or one whose localStorage got cleared) has no local id to
// compare against yet. Rather than silently trusting whatever the doc
// currently says, it claims the slot itself the same way an explicit
// sign-in would — which is what correctly forces out whichever OTHER
// browser was actually already holding it, instead of letting that
// takeover go unnoticed.
// ─────────────────────────────────────────────────────────────────────────

const registerSessionFn = httpsCallable(functions, 'registerSession');

function sessionKey(uid) {
  return `ll-session-id:${uid}`;
}

// Records the server sessionId that evicted this browser, so the "no local
// id yet" branch below can tell "I never had a claim" apart from "I just
// lost my claim to that exact id" — see startSessionGuard.
function evictedKey(uid) {
  return `ll-session-evicted:${uid}`;
}

// Non-null while a claim is in flight (the localStorage id is written, the
// server doesn't know about it yet). The guard must not judge ownership
// during this window: any snapshot it sees predates the claim, so comparing
// against it would sign out the very login that's mid-claim.
let pendingClaim = null;

export function registerNewLoginSession(uid) {
  const sessionId = crypto.randomUUID();
  // Written synchronously, before any await, so no interleaved guard
  // snapshot can observe this browser as claimless mid-login.
  localStorage.setItem(sessionKey(uid), sessionId);
  localStorage.removeItem(evictedKey(uid));

  const claim = registerSessionFn({ sessionId }).then(
    () => true,
    (err) => {
      // Never block a sign-in over this — but DO roll the local claim back.
      // Keeping an id the server never received is worse than holding none:
      // the guard would compare it against whatever the server actually has,
      // mismatch, and sign this browser out — turning one flaky network call
      // into a lockout. With no local id, the guard's claim branch simply
      // retries instead.
      console.warn('[session] registerSession failed:', err.message);
      if (localStorage.getItem(sessionKey(uid)) === sessionId) {
        localStorage.removeItem(sessionKey(uid));
      }
      return false;
    },
  );
  pendingClaim = claim;
  claim.finally(() => { if (pendingClaim === claim) pendingClaim = null; });
  return claim;
}

let unsubscribe = null;
let guardedUid = null;

/**
 * @param {string} uid
 * @param {{onForcedLogout?: () => void}} [opts] - called after this tab
 *   signs itself out because another device took over the account.
 */
export function startSessionGuard(uid, { onForcedLogout } = {}) {
  if (guardedUid === uid && unsubscribe) return; // already watching this account
  stopSessionGuard();
  guardedUid = uid;

  const ref = doc(db, 'users', uid, 'meta', 'session');
  unsubscribe = onSnapshot(ref, async (snap) => {
    const serverSessionId = snap.exists() ? snap.data().sessionId : null;

    if (pendingClaim) {
      // A claim is mid-flight, so this snapshot is necessarily older than it
      // and says nothing about who owns the slot now. Wait it out. On
      // success the claim's own write delivers a fresh snapshot that this
      // same handler judges properly, so drop this stale one. On failure the
      // claim rolled its localStorage id back, so fall through to the
      // claimless branch and retry rather than evicting ourselves.
      const claimed = await pendingClaim;
      if (claimed) return;
      if (guardedUid !== uid || !unsubscribe) return; // torn down while awaiting
    }

    const mine = localStorage.getItem(sessionKey(uid));

    if (!mine) {
      // This browser holds no claim on this uid's one session slot — it
      // predates this feature, its localStorage was cleared, or this is
      // the account's very first session ever. registerNewLoginSession
      // writes localStorage BEFORE its network call, so this branch can
      // never fire for a login this same tab just performed — only for a
      // tab that truly never registered one. Claim the slot now via the
      // same path an explicit sign-in uses: if some OTHER browser is
      // genuinely holding it, that browser's own listener will see the
      // resulting mismatch and sign itself out.
      //
      // Two things must be true first. (1) This tab is still actually signed
      // in as this uid — a tab that's already been evicted and is awaiting
      // signOut() must not grab the slot back. (2) This browser wasn't just
      // evicted BY the id the server currently holds. Without (2), a second
      // tab of the browser that lost the slot would see the first tab's
      // localStorage removal as "no claim," re-claim, and kick the winning
      // device off — the two devices then ping-pong forever.
      if (auth.currentUser?.uid !== uid) return;
      if (serverSessionId && localStorage.getItem(evictedKey(uid)) === serverSessionId) return;
      registerNewLoginSession(uid);
      return;
    }

    if (serverSessionId && serverSessionId !== mine) {
      stopSessionGuard();
      localStorage.removeItem(sessionKey(uid));
      localStorage.setItem(evictedKey(uid), serverSessionId);
      signOut(auth).finally(() => {
        if (onForcedLogout) onForcedLogout();
      });
    }
  }, (err) => {
    console.warn('[session] guard listener error:', err.message);
  });
}

export function stopSessionGuard() {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  guardedUid = null;
}
